import { forwardRef, type RefObject } from 'react';

import { Canvas, type CanvasRef } from '../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../core/components/canvas/CanvasContext.js';
import type { CanvasShape } from '../../core/components/canvas/types/canvas-shape.js';
import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { OVERLAY_PADDING } from '../../core/components/data-point/constants.js';
import { AreaChartDisplay } from '../components/displays/area/AreaChartDisplay.js';
import { BandChartDisplay } from '../components/displays/band/BandChartDisplay.js';
import { BarChartDisplay } from '../components/displays/bar/BarChartDisplay.js';
import { LineChartDisplay } from '../components/displays/line/LineChartDisplay.js';
import {
  canvasPlot,
  datapointCanvasPlot,
} from '../components/overlays/TimeseriesOverlays.sty.js';
import { useXScales } from '../hooks/useXScale.js';
import type {
  ChartVariant,
  TimeseriesPerVariant,
} from '../types/timeseries.js';

const displays = {
  line: LineChartDisplay,
  bar: BarChartDisplay,
  area: AreaChartDisplay,
};

interface TimeseriesGenericCanvasProps {
  dataPerVariant: TimeseriesPerVariant;
}

export const TimeseriesGenericCanvasLayout = forwardRef<
  CanvasRef[],
  TimeseriesGenericCanvasProps
>(({ dataPerVariant }: TimeseriesGenericCanvasProps, canvasRefs) => {
  const { currentScale: xScale } = useXScales();
  const { width, height } = useGridPlotSize();

  const handleRef = (el: CanvasRef | null, index: number) => {
    if (
      canvasRefs &&
      typeof canvasRefs !== 'function' &&
      canvasRefs.current &&
      el
    ) {
      canvasRefs.current[index] = el;
    }
  };
  return (
    <>
      {dataPerVariant.shapePriority.map((variant, index) => {
        let Display:
          | typeof LineChartDisplay
          | typeof BarChartDisplay
          | typeof AreaChartDisplay = LineChartDisplay;

        if (variant !== 'band') {
          Display = displays[variant as ChartVariant];
        }

        const isDottedShape = variant === 'line' || variant === 'area';

        const canvasContainerClassName = isDottedShape
          ? datapointCanvasPlot
          : canvasPlot;

        const canvasWidth =
          width && isDottedShape ? width + OVERLAY_PADDING : width;
        const canvasHeight =
          height && isDottedShape ? height + OVERLAY_PADDING : height;

        return (
          <div
            key={`highlight-display-${variant}${index}`}
            className={canvasContainerClassName}
          >
            <CanvasContext.Provider
              value={{
                shapeRefs: new Map<string, RefObject<CanvasShape>>(),
              }}
            >
              <Canvas
                ref={(el) => handleRef(el, index)}
                width={canvasWidth}
                height={canvasHeight}
              >
                {variant === 'band' ? (
                  <BandChartDisplay
                    series={dataPerVariant.band}
                    xScale={xScale}
                  />
                ) : (
                  <Display
                    series={dataPerVariant[variant as ChartVariant]}
                    xScale={xScale}
                  />
                )}
              </Canvas>
            </CanvasContext.Provider>
          </div>
        );
      })}
    </>
  );
});
